import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RotateCcw, Trash2, Upload } from 'lucide-react';
import {
  brandTokens,
  contrast,
  type BrandAssetSlot,
  type BrandingSettings,
  type CornerStyle,
  type FontChoice,
  type GeneralSettings,
  type PublicBranding,
} from '../../shared/branding';
import type { PlatformId } from '../../shared/types';
import { api, patch, post, uploadBrandAsset } from '../lib/api';
import { useConfig, useMe } from '../lib/session';
import { refreshTheme } from '../lib/theme';
import { useTitle } from '../components/AppShell';
import { errorMessage, useToast } from '../components/toast';
import { ConfirmDialog, Emphasis, Field, Notice, Spinner, StatusChip, Tabs } from '../components/ui';

interface SettingsView {
  branding: BrandingSettings;
  public: PublicBranding;
  general: GeneralSettings;
  system: {
    version: string;
    appUrl: string;
    passwordLogin: boolean;
    mailEnabled: boolean;
    mailHost: string | null;
    mailFrom: string | null;
    providers: string[];
    envAgencyDomains: string[];
    maxUploadMb: number;
  };
}

type Editable = Omit<BrandingSettings, 'assets'>;
const EDITABLE: (keyof Editable)[] = [
  'appName',
  'headerLabel',
  'accent',
  'ink',
  'success',
  'corners',
  'font',
  'fontName',
  'loginHeadline',
  'loginText',
  'providerName',
  'supportEmail',
  'imprintUrl',
  'privacyUrl',
  'showPoweredBy',
  'customCss',
];
const pickEditable = (b: BrandingSettings): Editable =>
  Object.fromEntries(EDITABLE.map((k) => [k, b[k]])) as unknown as Editable;

// ---------- Bausteine ----------

function Section({ title, hint, children }: { title: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <section className="settings-section">
      <div className="section-head" style={{ marginBottom: hint ? 6 : 16 }}>
        <h2 className="section-title">{title}</h2>
      </div>
      {hint && (
        <p className="small muted" style={{ marginBottom: 16 }}>
          {hint}
        </p>
      )}
      <div className="stack" style={{ gap: 16 }}>
        {children}
      </div>
    </section>
  );
}

function ColorField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  const valid = /^#[0-9a-fA-F]{6}$/.test(text);
  return (
    <Field label={label} hint={hint} error={valid ? null : 'Farbe als #RRGGBB angeben'}>
      {(id) => (
        <div className="color-field">
          <input
            type="color"
            aria-label={`${label} wählen`}
            value={valid ? text : value}
            onChange={(e) => onChange(e.target.value)}
          />
          <input
            id={id}
            className="input mono"
            value={text}
            maxLength={7}
            onChange={(e) => {
              setText(e.target.value);
              if (/^#[0-9a-fA-F]{6}$/.test(e.target.value)) onChange(e.target.value.toLowerCase());
            }}
          />
        </div>
      )}
    </Field>
  );
}

function AssetCard({
  title,
  hint,
  url,
  variant,
  accept,
  busy,
  onUpload,
  onRemove,
}: {
  title: string;
  hint: string;
  url: string | null;
  variant: 'light' | 'dark' | 'icon' | 'font';
  accept: string;
  busy: boolean;
  onUpload: (file: File) => void;
  onRemove: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="asset-card">
      <strong className="small">{title}</strong>
      <div className={`asset-preview ${variant === 'dark' ? 'dark' : ''}`}>
        {variant === 'font' ? (
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 22 }}>
            {url ? 'Aa Bb 123' : 'nicht gesetzt'}
          </span>
        ) : url ? (
          <img
            src={url}
            alt=""
            style={{
              maxWidth: variant === 'icon' ? 40 : '86%',
              maxHeight: variant === 'icon' ? 40 : 44,
              objectFit: 'contain',
            }}
          />
        ) : (
          <span className="small muted">Standard</span>
        )}
      </div>
      <span className="tiny muted">{hint}</span>
      <div className="row">
        <button
          type="button"
          className="btn btn-outline btn-xs"
          disabled={busy}
          onClick={() => input.current?.click()}
        >
          <Upload size={13} /> Hochladen
        </button>
        {url && (
          <button type="button" className="btn btn-ghost btn-xs" disabled={busy} onClick={onRemove}>
            <Trash2 size={13} /> Entfernen
          </button>
        )}
      </div>
      <input
        ref={input}
        type="file"
        hidden
        accept={accept}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onUpload(file);
          e.target.value = '';
        }}
      />
    </div>
  );
}

function MailTest({ email }: { email: string }) {
  const [state, setState] = useState<{ kind: 'idle' | 'busy' | 'ok' | 'error'; message?: string }>({
    kind: 'idle',
  });
  return (
    <div className="stack" style={{ gap: 10 }}>
      <button
        type="button"
        className="btn btn-outline"
        style={{ alignSelf: 'flex-start' }}
        disabled={state.kind === 'busy'}
        onClick={async () => {
          setState({ kind: 'busy' });
          try {
            await post('/api/settings/mail-test');
            setState({
              kind: 'ok',
              message: `Test-Mail an ${email} verschickt. Bitte auch im Spam-Ordner nachsehen.`,
            });
          } catch (err) {
            setState({ kind: 'error', message: errorMessage(err) });
          }
        }}
      >
        {state.kind === 'busy' ? 'Sende …' : 'Test-Mail an mich senden'}
      </button>
      {state.kind === 'ok' && <Notice kind="ok">{state.message}</Notice>}
      {state.kind === 'error' && <Notice kind="error">{state.message}</Notice>}
    </div>
  );
}

/** Miniatur der Oberfläche mit den Entwurfswerten – gleiche Ableitung wie das echte Theme. */
function BrandPreview({
  draft,
  logoUrl,
  logoDarkUrl,
}: {
  draft: BrandingSettings;
  logoUrl: string;
  logoDarkUrl: string | null;
}) {
  const style = { ...brandTokens(draft), fontFamily: 'var(--font-sans)' } as CSSProperties;
  return (
    <div className="brand-preview" style={style} aria-label="Vorschau des Erscheinungsbilds">
      <div className="bp-topbar">
        <img className="brand-logo" src={logoUrl} alt="" style={{ height: 20, maxWidth: 120 }} />
        {draft.headerLabel && (
          <span className="brand-label" style={{ fontSize: 11 }}>
            {draft.headerLabel}
          </span>
        )}
        <span className="bp-nav is-active">Kampagnen</span>
        <span className="bp-nav">Kunden</span>
        <span className="spacer" />
        <span className="avatar" style={{ width: 24, height: 24, fontSize: 10 }}>
          MC
        </span>
      </div>
      <div className="bp-body">
        <span className="eyebrow" style={{ fontSize: 10 }}>
          Nordlicht Reisen
        </span>
        <div className="bp-title">Goldener Herbst 2026</div>
        <div className="row-wrap">
          <span className="btn btn-primary btn-xs">Teilen</span>
          <span className="btn btn-outline btn-xs">Kundenansicht</span>
          <span className="link-arrow" style={{ fontSize: 11 }}>
            Ansehen
          </span>
        </div>
        <div className="row-wrap" style={{ gap: 6 }}>
          <StatusChip status="approved" />
          <StatusChip status="changes_requested" />
          <StatusChip status="pending" />
        </div>
        <div className="bp-card">
          <div className="bp-banner">
            <span className="pin" style={{ left: '64%', top: '42%' }}>
              1
            </span>
          </div>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <span className="small" style={{ fontWeight: 700 }}>
              Medium Rectangle
            </span>
            <span className="btn btn-approve btn-xs">Freigeben</span>
          </div>
        </div>
      </div>
      <div className="bp-login">
        <img
          className={`brand-logo ${logoDarkUrl ? '' : 'is-inverted'}`}
          src={logoDarkUrl ?? logoUrl}
          alt=""
          style={{ height: 18, maxWidth: 120 }}
        />
        <div className="bp-login-title">
          <Emphasis text={draft.loginHeadline || ' '} />
        </div>
      </div>
    </div>
  );
}

// ---------- Seite ----------

export function SettingsPage() {
  useTitle('Einstellungen');
  const { data: me } = useMe();
  const { data: config } = useConfig();
  const queryClient = useQueryClient();
  const toast = useToast();
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api<SettingsView>('/api/settings'),
    enabled: me?.role === 'admin',
  });
  const [tab, setTab] = useState<'look' | 'texts' | 'defaults' | 'system'>('look');
  const [draft, setDraft] = useState<BrandingSettings | null>(null);
  const [general, setGeneral] = useState<GeneralSettings | null>(null);
  const [domains, setDomains] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);

  useEffect(() => {
    if (!settings.data) return;
    setDraft((d) => d ?? settings.data.branding);
    setGeneral((g) => g ?? settings.data.general);
    setDomains((v) => v || settings.data.general.agencyDomains.join(', '));
  }, [settings.data]);

  if (me && me.role !== 'admin') {
    return (
      <div className="page page-narrow">
        <Notice kind="warn">Die Einstellungen können nur Admins ändern.</Notice>
      </div>
    );
  }
  if (settings.isLoading || !draft || !general || !settings.data) return <Spinner />;

  const view = settings.data;
  const dirty = JSON.stringify(pickEditable(draft)) !== JSON.stringify(pickEditable(view.branding));
  const set = <K extends keyof BrandingSettings>(key: K, value: BrandingSettings[K]) =>
    setDraft((d) => (d ? { ...d, [key]: value } : d));

  const apply = async (next: SettingsView, keepDraft: boolean) => {
    queryClient.setQueryData(['settings'], next);
    setDraft((d) =>
      keepDraft && d ? { ...d, assets: next.branding.assets, font: next.branding.font } : next.branding,
    );
    refreshTheme();
    await queryClient.invalidateQueries({ queryKey: ['config'] });
  };

  const run = async (fn: () => Promise<SettingsView>, message: string, keepDraft = false) => {
    setBusy(true);
    setError(null);
    try {
      await apply(await fn(), keepDraft);
      toast(message);
    } catch (err) {
      setError(errorMessage(err));
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  };

  const saveBranding = () =>
    run(
      () => patch<SettingsView>('/api/settings/branding', pickEditable(draft)),
      'Erscheinungsbild gespeichert.',
    );
  const upload = (slot: BrandAssetSlot, file: File) =>
    run(() => uploadBrandAsset<SettingsView>(slot, file), 'Datei übernommen.', true);
  const remove = (slot: BrandAssetSlot) =>
    run(
      () => api<SettingsView>(`/api/settings/branding/assets/${slot}`, { method: 'DELETE' }),
      'Datei entfernt.',
      true,
    );
  const saveGeneral = () =>
    run(
      async () => {
        const next = await patch<SettingsView>('/api/settings/general', {
          defaultPlatforms: general.defaultPlatforms,
          agencyDomains: domains
            .split(/[\s,;]+/)
            .map((d) => d.trim().replace(/^@/, ''))
            .filter(Boolean),
        });
        setGeneral(next.general);
        return next;
      },
      'Vorgaben gespeichert.',
      true,
    );

  const assetUrl = (slot: BrandAssetSlot) => (draft.assets[slot] ? `/branding/${draft.assets[slot]}` : null);
  const logoUrl = assetUrl('logo') ?? '/theme/logo';
  const accentOnWhite = contrast(draft.accent, '#ffffff');

  return (
    <div className="page">
      <div className="page-head">
        <div className="page-head-text">
          <span className="eyebrow">Verwaltung</span>
          <h1 className="page-title">Einstellungen</h1>
          <p className="lead">
            Erscheinungsbild und Vorgaben dieser Instanz. Änderungen gelten sofort für alle – auch auf
            Freigabe-Links und der Anmeldeseite.
          </p>
        </div>
      </div>

      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { value: 'look', label: 'Erscheinungsbild' },
          { value: 'texts', label: 'Texte & Rechtliches' },
          { value: 'defaults', label: 'Vorgaben' },
          { value: 'system', label: 'System' },
        ]}
      />
      {error && (
        <div style={{ marginTop: 16 }}>
          <Notice kind="error">{error}</Notice>
        </div>
      )}

      <div className="settings-layout">
        <div>
          {tab === 'look' && (
            <>
              <Section title="Name">
                <div className="form-grid two">
                  <Field
                    label="Produktname"
                    hint="Im Browser-Tab, in Einladungs-Mails und auf der Anmeldeseite."
                  >
                    {(id) => (
                      <input
                        id={id}
                        className="input"
                        value={draft.appName}
                        maxLength={60}
                        onChange={(e) => set('appName', e.target.value)}
                      />
                    )}
                  </Field>
                  <Field
                    label="Zusatz neben dem Logo (optional)"
                    hint="z. B. „Banner-Review“. Leer, wenn das Logo den Namen schon enthält."
                  >
                    {(id) => (
                      <input
                        id={id}
                        className="input"
                        value={draft.headerLabel}
                        maxLength={40}
                        onChange={(e) => set('headerLabel', e.target.value)}
                      />
                    )}
                  </Field>
                </div>
              </Section>

              <Section
                title="Logos"
                hint="Logos werden sofort übernommen. SVG ist ideal, PNG oder WebP gehen auch."
              >
                <div className="asset-grid">
                  <AssetCard
                    title="Logo"
                    hint="Kopfzeile, Freigabe-Links · max. 2 MB"
                    url={assetUrl('logo')}
                    variant="light"
                    accept=".svg,.png,.jpg,.jpeg,.webp"
                    busy={busy}
                    onUpload={(f) => upload('logo', f)}
                    onRemove={() => remove('logo')}
                  />
                  <AssetCard
                    title="Logo auf dunkel"
                    hint="Anmeldeseite · ohne eigenes wird das Logo weiß eingefärbt"
                    url={assetUrl('logoDark')}
                    variant="dark"
                    accept=".svg,.png,.jpg,.jpeg,.webp"
                    busy={busy}
                    onUpload={(f) => upload('logoDark', f)}
                    onRemove={() => remove('logoDark')}
                  />
                  <AssetCard
                    title="Favicon"
                    hint="Browser-Tab · quadratisch, SVG/PNG/ICO"
                    url={assetUrl('favicon')}
                    variant="icon"
                    accept=".svg,.png,.ico"
                    busy={busy}
                    onUpload={(f) => upload('favicon', f)}
                    onRemove={() => remove('favicon')}
                  />
                </div>
              </Section>

              <Section
                title="Farben"
                hint="Aus drei Farben leitet ZeigBanner alle Abstufungen ab – Linien, Flächen, Schrifttöne."
              >
                <div className="form-grid two">
                  <ColorField
                    label="Akzentfarbe"
                    hint="Links, aktive Navigation, Markierungen, Hover"
                    value={draft.accent}
                    onChange={(v) => set('accent', v)}
                  />
                  <ColorField
                    label="Grundfarbe"
                    hint="Schrift, Hauptbuttons, Trennlinien"
                    value={draft.ink}
                    onChange={(v) => set('ink', v)}
                  />
                  <ColorField
                    label="Farbe für „Freigegeben“"
                    hint="Helle Farben werden zur Fläche, kräftige zur Schrift"
                    value={draft.success}
                    onChange={(v) => set('success', v)}
                  />
                </div>
                {accentOnWhite < 3 && (
                  <Notice kind="warn">
                    Die Akzentfarbe ist auf Weiß schwer lesbar (Kontrast{' '}
                    {accentOnWhite.toFixed(1).replace('.', ',')}:1). Für Links besser eine dunklere Farbe
                    wählen.
                  </Notice>
                )}
                {contrast(draft.ink, '#ffffff') < 7 && (
                  <Notice kind="warn">
                    Die Grundfarbe ist für Fließtext recht hell. Empfohlen ist ein sehr dunkler Ton.
                  </Notice>
                )}
              </Section>

              <Section title="Form und Schrift">
                <div className="field">
                  <span className="field-label">Ecken</span>
                  <div
                    className="segmented"
                    role="group"
                    aria-label="Ecken"
                    style={{ alignSelf: 'flex-start' }}
                  >
                    {(
                      [
                        ['square', 'Eckig'],
                        ['soft', 'Leicht gerundet'],
                        ['round', 'Rund'],
                      ] as [CornerStyle, string][]
                    ).map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        aria-pressed={draft.corners === value}
                        onClick={() => set('corners', value)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="field">
                  <span className="field-label">Schrift</span>
                  <div
                    className="segmented"
                    role="group"
                    aria-label="Schrift"
                    style={{ alignSelf: 'flex-start' }}
                  >
                    {(
                      [
                        ['dm-sans', 'DM Sans'],
                        ['system', 'Systemschrift'],
                        ['custom', 'Eigene Schrift'],
                      ] as [FontChoice, string][]
                    ).map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        aria-pressed={draft.font === value}
                        onClick={() => set('font', value)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <span className="field-hint">
                    DM Sans ist frei lizenziert und im Tool enthalten. Eine Hausschrift nur hochladen, wenn
                    ihre Lizenz die Nutzung im Web erlaubt – die Dateien bleiben auf diesem Server.
                  </span>
                </div>
                {draft.font === 'custom' && (
                  <>
                    <div className="asset-grid two">
                      <AssetCard
                        title="Schrift normal"
                        hint="WOFF2, WOFF, TTF oder OTF · max. 4 MB"
                        url={assetUrl('fontRegular')}
                        variant="font"
                        accept=".woff2,.woff,.ttf,.otf"
                        busy={busy}
                        onUpload={(f) => upload('fontRegular', f)}
                        onRemove={() => remove('fontRegular')}
                      />
                      <AssetCard
                        title="Schrift fett (optional)"
                        hint="für Überschriften und Buttons"
                        url={assetUrl('fontBold')}
                        variant="font"
                        accept=".woff2,.woff,.ttf,.otf"
                        busy={busy}
                        onUpload={(f) => upload('fontBold', f)}
                        onRemove={() => remove('fontBold')}
                      />
                    </div>
                    {!draft.assets.fontRegular && (
                      <Notice kind="warn">
                        Noch keine Schriftdatei hochgeladen – bis dahin gilt DM Sans.
                      </Notice>
                    )}
                  </>
                )}
              </Section>

              <Section
                title="Eigenes CSS"
                hint="Für Feinheiten, die über Farben und Ecken hinausgehen. Wird nach dem Theme geladen."
              >
                <textarea
                  className="textarea mono"
                  style={{ minHeight: 140, fontSize: 13 }}
                  spellCheck={false}
                  value={draft.customCss}
                  placeholder={':root .chip-approved {\n  text-transform: none;\n}'}
                  onChange={(e) => set('customCss', e.target.value)}
                />
              </Section>

              <div className="row" style={{ marginTop: 32 }}>
                <button type="button" className="btn btn-ghost" onClick={() => setConfirmReset(true)}>
                  <RotateCcw size={16} /> Auf ZeigBanner-Standard zurücksetzen
                </button>
              </div>
            </>
          )}

          {tab === 'texts' && (
            <>
              <Section title="Anmeldeseite" hint="Text zwischen *Sternchen* erscheint in der Akzentfarbe.">
                <Field label="Überschrift">
                  {(id) => (
                    <input
                      id={id}
                      className="input"
                      value={draft.loginHeadline}
                      maxLength={120}
                      onChange={(e) => set('loginHeadline', e.target.value)}
                    />
                  )}
                </Field>
                <Field label="Text">
                  {(id) => (
                    <textarea
                      id={id}
                      className="textarea"
                      value={draft.loginText}
                      maxLength={400}
                      onChange={(e) => set('loginText', e.target.value)}
                    />
                  )}
                </Field>
              </Section>
              <Section title="Freigabe-Links und Fußzeile">
                <div className="form-grid two">
                  <Field
                    label="Anbieter"
                    hint="z. B. der Agenturname – erscheint in der Fußzeile und bei Gästen."
                  >
                    {(id) => (
                      <input
                        id={id}
                        className="input"
                        value={draft.providerName}
                        maxLength={60}
                        onChange={(e) => set('providerName', e.target.value)}
                      />
                    )}
                  </Field>
                  <Field label="Kontakt-E-Mail (optional)" hint="Für Rückfragen von Kund:innen.">
                    {(id) => (
                      <input
                        id={id}
                        className="input"
                        type="email"
                        value={draft.supportEmail}
                        onChange={(e) => set('supportEmail', e.target.value)}
                      />
                    )}
                  </Field>
                </div>
                <label className="check">
                  <input
                    type="checkbox"
                    checked={draft.showPoweredBy}
                    onChange={(e) => set('showPoweredBy', e.target.checked)}
                  />
                  <span>
                    Hinweis „Läuft mit ZeigBanner“ anzeigen
                    <span className="field-hint" style={{ display: 'block' }}>
                      Kleiner Vermerk in der Fußzeile. Für reines White-Label abschalten.
                    </span>
                  </span>
                </label>
              </Section>
              <Section
                title="Rechtliches"
                hint="Für eine öffentlich erreichbare Instanz sind Impressum und Datenschutzerklärung Pflicht."
              >
                <div className="form-grid two">
                  <Field label="Impressum (Adresse)">
                    {(id) => (
                      <input
                        id={id}
                        className="input"
                        placeholder="https://…"
                        value={draft.imprintUrl}
                        onChange={(e) => set('imprintUrl', e.target.value)}
                      />
                    )}
                  </Field>
                  <Field label="Datenschutzerklärung (Adresse)">
                    {(id) => (
                      <input
                        id={id}
                        className="input"
                        placeholder="https://…"
                        value={draft.privacyUrl}
                        onChange={(e) => set('privacyUrl', e.target.value)}
                      />
                    )}
                  </Field>
                </div>
              </Section>
            </>
          )}

          {tab === 'defaults' && (
            <>
              <Section title="Neue Kampagnen" hint="Vorauswahl der Zielplattformen für den Technik-Check.">
                <div className="form-grid two" style={{ gap: 8 }}>
                  {config?.platforms.map((p) => (
                    <label key={p.id} className="check">
                      <input
                        type="checkbox"
                        checked={general.defaultPlatforms.includes(p.id)}
                        onChange={(e) =>
                          setGeneral((g) =>
                            g
                              ? {
                                  ...g,
                                  defaultPlatforms: e.target.checked
                                    ? [...g.defaultPlatforms, p.id]
                                    : g.defaultPlatforms.filter((x: PlatformId) => x !== p.id),
                                }
                              : g,
                          )
                        }
                      />
                      {p.name}
                    </label>
                  ))}
                </div>
              </Section>
              <Section
                title="Agentur-Domains"
                hint="Wer sich per Microsoft oder SSO mit einer dieser Domains anmeldet, bekommt automatisch ein Agentur-Konto."
              >
                <Field label="Domains (kommagetrennt)">
                  {(id) => (
                    <input
                      id={id}
                      className="input"
                      placeholder="agentur.de"
                      value={domains}
                      onChange={(e) => setDomains(e.target.value)}
                    />
                  )}
                </Field>
                {view.system.envAgencyDomains.length > 0 && (
                  <p className="small muted">
                    Zusätzlich aus der Server-Konfiguration: {view.system.envAgencyDomains.join(', ')}
                  </p>
                )}
              </Section>
              <div style={{ marginTop: 24 }}>
                <button type="button" className="btn btn-primary" disabled={busy} onClick={saveGeneral}>
                  Vorgaben speichern
                </button>
              </div>
            </>
          )}

          {tab === 'system' && (
            <>
              <Section
                title="Mailversand"
                hint="Für Einladungen, Passwort-Links und Benachrichtigungen bei Kundenfeedback. Zugangsdaten stehen in der Server-Konfiguration (SMTP_HOST, SMTP_USER, SMTP_PASSWORD, MAIL_FROM)."
              >
                {view.system.mailEnabled ? (
                  <>
                    <dl className="kv">
                      <dt>Server</dt>
                      <dd className="mono">{view.system.mailHost ?? '–'}</dd>
                      <dt>Absender</dt>
                      <dd>
                        {view.public.appName} &lt;{view.system.mailFrom}&gt;
                      </dd>
                    </dl>
                    <MailTest email={me?.email ?? ''} />
                  </>
                ) : (
                  <Notice kind="warn">
                    Noch nicht eingerichtet. Bis dahin erscheinen Einladungs- und Passwort-Links zum Kopieren;
                    Benachrichtigungen gibt es nur in der App. Anleitung: docs/coolify.md, Abschnitt
                    „Mailversand“.
                  </Notice>
                )}
              </Section>
              <Section
                title="System"
                hint="Diese Werte stehen in der Server-Konfiguration (Umgebungsvariablen, z. B. in Coolify)."
              >
                <dl className="kv">
                  <dt>Version</dt>
                  <dd>ZeigBanner {view.system.version}</dd>
                  <dt>Adresse</dt>
                  <dd>{view.system.appUrl}</dd>
                  <dt>Anmeldung mit Passwort</dt>
                  <dd>{view.system.passwordLogin ? 'aktiv' : 'abgeschaltet'}</dd>
                  <dt>Single Sign-on</dt>
                  <dd>
                    {view.system.providers.length
                      ? view.system.providers.join(', ')
                      : 'nicht eingerichtet (siehe docs/microsoft-sso.md)'}
                  </dd>
                  <dt>Upload-Grenze</dt>
                  <dd>{view.system.maxUploadMb} MB pro Datei</dd>
                </dl>
              </Section>
            </>
          )}
        </div>

        {(tab === 'look' || tab === 'texts') && (
          <aside className="settings-preview">
            <span className="eyebrow">Vorschau</span>
            <BrandPreview draft={draft} logoUrl={logoUrl} logoDarkUrl={assetUrl('logoDark')} />
          </aside>
        )}
      </div>

      {(tab === 'look' || tab === 'texts') && (
        <div className={`save-bar ${dirty ? 'is-visible' : ''}`} aria-hidden={!dirty}>
          <span className="small">Ungespeicherte Änderungen</span>
          <span className="spacer" />
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDraft(view.branding)}>
            Verwerfen
          </button>
          <button
            type="button"
            className="btn btn-accent btn-sm"
            disabled={busy || !draft.appName.trim()}
            onClick={saveBranding}
          >
            Speichern
          </button>
        </div>
      )}

      <ConfirmDialog
        open={confirmReset}
        onClose={() => setConfirmReset(false)}
        title="Auf Standard zurücksetzen?"
        message="Name, Farben, Logos, Schrift, Texte und eigenes CSS gehen auf das neutrale ZeigBanner-Erscheinungsbild zurück. Hochgeladene Dateien werden gelöscht."
        confirmLabel="Zurücksetzen"
        onConfirm={() =>
          run(() => post<SettingsView>('/api/settings/branding/reset'), 'Auf Standard zurückgesetzt.')
        }
      />
    </div>
  );
}
